import { useEffect, useState } from 'react';
import { api, type OidcConnection } from '../api';
import { Banner, Card, CheckBox, Copyable, Json, SelectField, TextField } from '../components/ui';
import { ConnectionPicker, NoConnections, useConnections } from '../components/useConnections';

export function OidcTab({ tenantId }: { tenantId: string }) {
  const base = `/api/tenants/${tenantId}/oidc`;
  const conn = useConnections<OidcConnection>(base);
  const [draft, setDraft] = useState<OidcConnection | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setDraft(conn.selected ? { ...conn.selected } : null); setSaved(false); }, [conn.selected]);

  if (conn.items === null) return <p className="faint">Loading…</p>;
  if (conn.items.length === 0) {
    return <NoConnections what="OIDC connection" onCreate={conn.create} busy={conn.busy} />;
  }

  const set = <K extends keyof OidcConnection>(key: K, value: OidcConnection[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  const dirty = !!draft && !!conn.selected && JSON.stringify(draft) !== JSON.stringify(conn.selected);

  return (
    <>
      <ConnectionPicker
        items={conn.items} selectedId={conn.selectedId} onSelect={conn.setSelectedId}
        onCreate={conn.create} busy={conn.busy} newLabel="Connection name"
      />

      {conn.error && <Banner kind="error">{conn.error}</Banner>}
      {saved && !dirty && <Banner kind="success">Saved.</Banner>}

      {draft && (
        <>
          <Card
            title="Register these at your provider"
            hint="The redirect URI must match byte for byte, including scheme, port and trailing path."
          >
            <Copyable label="Redirect URI" value={draft.endpoints.redirectUri} />
            <Copyable label="Post-logout redirect URI" value={draft.endpoints.postLogoutRedirectUri} />
            <div className="row" style={{ marginTop: 14 }}>
              <a className="badge proto" href={draft.endpoints.loginUrl} target="_blank" rel="noreferrer">
                Run authorization code flow ↗
              </a>
              <span className="faint small">
                Redirects to the provider, then reports on the tokens that come back.
              </span>
            </div>
          </Card>

          <Card title="Provider">
            <div className="grid two">
              <TextField
                label="Issuer" value={draft.issuer} onChange={(v) => set('issuer', v)} mono
                placeholder="https://pingfederate.example.com"
                hint="Compared against the iss claim in the id_token."
              />
              <TextField
                label="Discovery URL override" value={draft.discoveryUrl} onChange={(v) => set('discoveryUrl', v)} mono
                placeholder="defaults to <issuer>/.well-known/openid-configuration"
              />
            </div>
            <CheckBox
              label="Use discovery" checked={draft.useDiscovery} onChange={(v) => set('useDiscovery', v)}
              hint="Endpoints below are only needed when discovery is off, or to override what it returns."
            />
            <Discovery base={base} id={draft.id} />

            <div className="divider" />
            <div className="grid two">
              <TextField label="Authorization endpoint" value={draft.authorizationEndpoint}
                onChange={(v) => set('authorizationEndpoint', v)} mono placeholder="from discovery" />
              <TextField label="Token endpoint" value={draft.tokenEndpoint}
                onChange={(v) => set('tokenEndpoint', v)} mono placeholder="from discovery" />
              <TextField label="UserInfo endpoint" value={draft.userinfoEndpoint}
                onChange={(v) => set('userinfoEndpoint', v)} mono placeholder="from discovery" />
              <TextField label="JWKS URI" value={draft.jwksUri}
                onChange={(v) => set('jwksUri', v)} mono placeholder="from discovery" />
              <TextField label="Introspection endpoint" value={draft.introspectionEndpoint}
                onChange={(v) => set('introspectionEndpoint', v)} mono placeholder="from discovery" />
              <TextField label="End session endpoint" value={draft.endSessionEndpoint}
                onChange={(v) => set('endSessionEndpoint', v)} mono placeholder="from discovery" />
            </div>
          </Card>

          <Card title="Client">
            <div className="grid two">
              <TextField label="Client ID" value={draft.clientId} onChange={(v) => set('clientId', v)} mono />
              <TextField
                label="Client secret" value={draft.clientSecret} onChange={(v) => set('clientSecret', v)}
                type="password" hint="Stored server-side. Leave the masked value untouched to keep the current secret."
              />
              <SelectField
                label="Token endpoint auth method" value={draft.tokenAuthMethod}
                onChange={(v) => set('tokenAuthMethod', v)}
                options={[
                  { value: 'client_secret_basic', label: 'client_secret_basic' },
                  { value: 'client_secret_post', label: 'client_secret_post' },
                  { value: 'none', label: 'none (public client)' },
                ]}
              />
              <SelectField
                label="PKCE" value={draft.pkceMethod} onChange={(v) => set('pkceMethod', v)}
                options={[
                  { value: 'S256', label: 'S256 (recommended)' },
                  { value: 'plain', label: 'plain' },
                  { value: 'none', label: 'off' },
                ]}
              />
              <TextField
                label="Scopes" value={draft.scopes} onChange={(v) => set('scopes', v)} mono
                placeholder="openid profile email" hint="Space separated."
              />
              <TextField
                label="Expected audience" value={draft.expectedAudience}
                onChange={(v) => set('expectedAudience', v)} mono placeholder="defaults to the client ID"
              />
              <SelectField
                label="Response mode" value={draft.responseMode} onChange={(v) => set('responseMode', v)}
                options={[
                  { value: '', label: 'provider default' },
                  { value: 'query', label: 'query' },
                  { value: 'form_post', label: 'form_post' },
                  { value: 'fragment', label: 'fragment (not supported server side)' },
                ]}
              />
              <TextField
                label="Clock skew tolerance (s)" value={String(draft.clockSkewS)}
                onChange={(v) => set('clockSkewS', Number(v) || 0)} type="number"
              />
            </div>
            <CheckBox
              label="Verify id_token signature against JWKS" checked={draft.verifyIdToken}
              onChange={(v) => set('verifyIdToken', v)}
              hint="Turn off only to inspect a token you already know is broken."
            />
            <CheckBox
              label="Call the UserInfo endpoint" checked={draft.fetchUserinfo}
              onChange={(v) => set('fetchUserinfo', v)}
            />
            <CheckBox label="Enabled" checked={draft.enabled} onChange={(v) => set('enabled', v)} />
          </Card>

          <ExtraGrants base={base} id={draft.id} />

          <div className="row" style={{ marginBottom: 30 }}>
            <button
              className="primary" disabled={!dirty || conn.busy}
              onClick={async () => { if (await conn.save(draft.id, draft)) setSaved(true); }}
            >{conn.busy ? 'Saving…' : 'Save changes'}</button>
            <button disabled={!dirty} onClick={() => setDraft({ ...conn.selected! })}>Discard</button>
            <div className="spacer" />
            <button
              className="danger"
              onClick={() => { if (confirm(`Delete OIDC connection "${draft.name}"?`)) void conn.remove(draft.id); }}
            >Delete connection</button>
          </div>
        </>
      )}
    </>
  );
}

function Discovery({ base, id }: { base: string; id: string }) {
  const [doc, setDoc] = useState<unknown>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <div style={{ marginTop: 8 }}>
      <div className="row">
        <button
          disabled={busy}
          onClick={async () => {
            setBusy(true); setError(''); setDoc(null);
            try {
              const r = await api.post<{ document: unknown }>(`${base}/${id}/discover`, {});
              setDoc(r.document);
            } catch (e) {
              setError(e instanceof Error ? e.message : 'Discovery failed');
            } finally { setBusy(false); }
          }}
        >{busy ? 'Fetching…' : 'Test discovery'}</button>
        {doc != null && <button className="ghost" onClick={() => setDoc(null)}>Hide</button>}
      </div>
      {error && <div style={{ marginTop: 10 }}><Banner kind="error">{error}</Banner></div>}
      {doc != null && <div style={{ marginTop: 10 }}><Json value={doc} /></div>}
    </div>
  );
}

function ExtraGrants({ base, id }: { base: string; id: string }) {
  const [out, setOut] = useState<unknown>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [introspectToken, setIntrospectToken] = useState('');

  const call = async (label: string, path: string, body: unknown) => {
    setBusy(label); setError(''); setOut(null);
    try {
      setOut(await api.post(`${base}/${id}${path}`, body));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally { setBusy(''); }
  };

  return (
    <Card title="Other grants and endpoints" hint="Run these without a browser round trip.">
      <div className="row" style={{ marginBottom: 14 }}>
        <button disabled={!!busy} onClick={() => call('cc', '/client-credentials', {})}>
          {busy === 'cc' ? 'Running…' : 'Test client_credentials'}
        </button>
      </div>

      <div className="grid two">
        <div>
          <TextField label="Refresh token" value={refreshToken} onChange={setRefreshToken} mono
            placeholder="paste a refresh_token from an earlier result" />
          <button disabled={!!busy || !refreshToken} onClick={() => call('rt', '/refresh', { refreshToken })}>
            {busy === 'rt' ? 'Running…' : 'Exchange refresh token'}
          </button>
        </div>
        <div>
          <TextField label="Token to introspect" value={introspectToken} onChange={setIntrospectToken} mono
            placeholder="access token" />
          <button disabled={!!busy || !introspectToken} onClick={() => call('in', '/introspect', { token: introspectToken })}>
            {busy === 'in' ? 'Running…' : 'Introspect (RFC 7662)'}
          </button>
        </div>
      </div>

      {error && <div style={{ marginTop: 12 }}><Banner kind="error">{error}</Banner></div>}
      {out != null && <div style={{ marginTop: 12 }}><Json value={out} /></div>}
    </Card>
  );
}
